import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Calendar, TrendingUp, Plane, ChevronRight, HelpCircle } from 'lucide-react';

const HowItWorksPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const steps = [
    {
      number: '01',
      icon: <MapPin className="text-[#8ca87c]" size={32} />,
      title: 'Choose Your Destination',
      description: 'Select the Algarve as your padel paradise. Browse our packages and find the experience that matches your preferences.',
      image: 'https://images.unsplash.com/photo-1563393826939-c82318ec3fcd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxBbGdhcnZlJTIwUG9ydHVnYWwlMjBiZWFjaCUyMHN1bnNldCUyMGxhbmRzY2FwZSUyMG5vJTIwcGVvcGxlfGVufDB8fHx8MTc3MDU1MjAxN3ww&ixlib=rb-4.1.0&q=85',
    },
    {
      number: '02',
      icon: <TrendingUp className="text-[#8ca87c]" size={32} />,
      title: 'Select Your Level & Package',
      description: 'Tell us about your padel experience. We match you with players at your level and customize the coaching intensity.',
      image: 'https://customer-assets.emergentagent.com/job_517a97fe-c059-48a3-bb5f-cd3bbb5bb437/artifacts/bw60o3z0_WhatsApp%20Image%202026-02-07%20at%2012.40.46%20%281%29.jpeg',
    },
    {
      number: '03',
      icon: <Calendar className="text-[#8ca87c]" size={32} />,
      title: 'Pick Your Dates',
      description: 'Choose from our scheduled departures or request custom dates for groups. We run trips year-round.',
      image: 'https://images.unsplash.com/photo-1759783640223-a893fa1e7e1b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxMYWdvcyUyMFBvcnR1Z2FsJTIwY2xpZmZzJTIwb2NlYW4lMjB0dXJxdW9pc2UlMjB3YXRlcnxlbnwwfHx8fDE3NzA1NTIwMjd8MA&ixlib=rb-4.1.0&q=85',
    },
    {
      number: '04',
      icon: <Plane className="text-[#8ca87c]" size={32} />,
      title: 'Travel & Play',
      description: 'Arrive in Faro, and we take care of the rest. Transfers, accommodation, meals, courts - just focus on playing and enjoying.',
      image: 'https://customer-assets.emergentagent.com/job_517a97fe-c059-48a3-bb5f-cd3bbb5bb437/artifacts/yuxqiqmr_WhatsApp%20Image%202026-02-07%20at%2012.40.45.jpeg',
    },
  ];

  const faqs = [
    {
      question: 'What skill level do I need?',
      answer: 'We welcome players of all levels, from beginners to advanced. Our level-matching system ensures you play with others at your skill level.',
    },
    {
      question: 'Do I need to bring my own equipment?',
      answer: 'No! Premium padel rackets and balls are included in all packages. Just bring comfortable sportswear and shoes.',
    },
    {
      question: 'Can I come alone?',
      answer: 'Absolutely! Many of our guests travel solo and make great friends during the trip. Our community aspect is a big part of the experience.',
    },
    {
      question: 'What\'s the group size?',
      answer: 'Groups typically range from 8-16 players, ensuring a social atmosphere while maintaining quality coaching and court time.',
    },
    {
      question: 'What airport should I fly into?',
      answer: 'Faro Airport (FAO) is the closest airport to our Algarve location. We provide transfers from/to the airport.',
    },
    {
      question: 'What\'s included in the price?',
      answer: 'All packages include accommodation, meals (varies by package), padel sessions, coaching, equipment, transfers, and planned experiences.',
    },
  ];

  const typicalDay = [
    { time: '08:00', activity: 'Breakfast at the hotel' },
    { time: '09:30', activity: 'Morning padel session with coaching' },
    { time: '12:00', activity: 'Cool down & recovery' },
    { time: '13:00', activity: 'Lunch break' },
    { time: '15:00', activity: 'Free time: beach, pool, or explore' },
    { time: '17:00', activity: 'Afternoon matches' },
    { time: '19:30', activity: 'Group dinner' },
    { time: '21:30', activity: 'Optional: nightlife or relax' },
  ];

  return (
    <div className="bg-[#f5f3ee]" data-testid="how-it-works-page">
      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden pt-24" data-testid="hiw-hero">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_517a97fe-c059-48a3-bb5f-cd3bbb5bb437/artifacts/ofmg81ra_WhatsApp%20Image%202026-02-08%20at%2022.04.04%20%287%29.jpeg"
            alt="Pool and padel courts"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center py-16">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block px-5 py-2 border border-[#f5f3ee]/40 text-[#f5f3ee] text-xs tracking-[0.3em] uppercase mb-8">
              Simple Process
            </span>
            <h1 className="font-heading font-light text-4xl sm:text-5xl md:text-6xl text-[#f5f3ee] mb-6">
              How It Works
            </h1>
            <p className="text-lg text-[#f5f3ee]/80 max-w-xl mx-auto font-light">
              From booking to playing, we've made the entire process seamless. 
              Here's how to plan your perfect padel holiday.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-24 md:py-32" data-testid="steps-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="space-y-24 md:space-y-32">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                data-testid={`step-${index + 1}`}
              >
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-4 mb-6">
                    <span className="font-heading font-light text-6xl text-[#8ca87c]/30">
                      {step.number}
                    </span>
                    <div className="w-16 h-16 bg-white flex items-center justify-center">
                      {step.icon}
                    </div>
                  </div>
                  <h2 className="font-heading font-light text-3xl sm:text-4xl text-[#414b3b] mb-6">
                    {step.title}
                  </h2>
                  <p className="text-base text-[#414b3b]/60 font-light leading-relaxed">
                    {step.description}
                  </p>
                </div>

                {/* Image */}
                <div className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="overflow-hidden aspect-[4/3]">
                    <img
                      src={step.image}
                      alt={step.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  {/* Decorative element */}
                  <div className={`absolute -z-10 ${index % 2 === 0 ? '-bottom-4 -right-4' : '-bottom-4 -left-4'} w-full h-full bg-[#8ca87c]/20`} />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What to Expect Timeline */}
      <section className="py-24 md:py-32 bg-white" data-testid="timeline-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="text-xs font-medium tracking-[0.3em] uppercase text-[#8ca87c] mb-6 block">
              Your Trip
            </span>
            <h2 className="font-heading font-light text-3xl sm:text-4xl text-[#414b3b] mb-6">
              A Typical Day
            </h2>
            <p className="text-base text-[#414b3b]/60 max-w-2xl mx-auto font-light">
              Here's what a typical day looks like on a Padel Holidays experience.
            </p>
          </motion.div>

          <div className="max-w-3xl mx-auto">
            {typicalDay.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-6 py-4 border-b border-[#414b3b]/10 last:border-0"
                data-testid={`timeline-item-${index}`}
              >
                <span className="font-heading text-lg text-[#8ca87c] w-16">
                  {item.time}
                </span>
                <span className="text-[#414b3b]/70 font-light">{item.activity}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-24 md:py-32 bg-[#f5f3ee]" data-testid="faq-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="text-xs font-medium tracking-[0.3em] uppercase text-[#8ca87c] mb-6 block">
              Questions?
            </span>
            <h2 className="font-heading font-light text-3xl sm:text-4xl text-[#414b3b] mb-6">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white p-6"
                data-testid={`faq-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#8ca87c]/10 flex items-center justify-center flex-shrink-0">
                    <HelpCircle size={20} className="text-[#8ca87c]" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg text-[#414b3b] mb-2">
                      {faq.question}
                    </h3>
                    <p className="text-[#414b3b]/60 font-light leading-relaxed text-sm">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#414b3b]" data-testid="hiw-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-heading font-light text-3xl sm:text-4xl text-[#f5f3ee] mb-6">
              Ready to Start?
            </h2>
            <p className="text-base text-[#f5f3ee]/60 mb-10 font-light">
              Have more questions? Get in touch and we'll help you plan the perfect trip.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="h-14 px-10 bg-[#8ca87c] text-[#414b3b] font-light text-sm tracking-[0.15em] uppercase flex items-center justify-center gap-3 transition-all duration-300 hover:bg-[#f5f3ee]"
                data-testid="hiw-book-btn"
              >
                Book Your Trip
                <ChevronRight size={18} />
              </Link>
              <Link
                to="/experiences"
                className="h-14 px-10 border border-[#f5f3ee]/30 text-[#f5f3ee] font-light text-sm tracking-[0.15em] uppercase flex items-center justify-center transition-all duration-300 hover:border-[#f5f3ee] hover:bg-[#f5f3ee]/10"
                data-testid="hiw-packages-btn"
              >
                View Packages
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorksPage;
